import { FC } from 'react';

export type AddressBookErrorDetailsProps = {
  organisationId?: number;
  addressBookId?: string;
  error: unknown;
};

const formatError = (error: unknown): string => {
  if (error === undefined || error === null) {
    return '(no error payload)';
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
};

/**
 * Collapsible technical detail for the address-book load failure. Surfaces the
 * ids that were sent, where each came from, and the raw error payload so the
 * failure can be diagnosed (e.g. handed to the backend) without guesswork.
 */
export const AddressBookErrorDetails: FC<AddressBookErrorDetailsProps> = ({
  organisationId,
  addressBookId,
  error,
}) => (
  <details className="text-xs">
    <summary className="cursor-pointer font-medium select-none">
      Technical details
    </summary>
    <dl className="mt-2 flex flex-col gap-3 rounded-lg bg-gray-50 p-4 text-gray-700">
      <div>
        <dt className="font-semibold">Organisation ID</dt>
        <dd className="font-mono break-all">{organisationId ?? '(unknown)'}</dd>
        <dd className="text-gray-500 italic">from the current organisation</dd>
      </div>
      <div>
        <dt className="font-semibold">Default address book ID</dt>
        <dd className="font-mono break-all">{addressBookId ?? '(unknown)'}</dd>
        <dd className="text-gray-500 italic">
          from the organisation policy
          (addressBook.defaultAddressBook.addressBookId)
        </dd>
      </div>
      <div>
        <dt className="font-semibold">Request</dt>
        <dd className="font-mono break-all">
          GET /address-book?actionType=getAddressBookWithContacts
        </dd>
      </div>
      <div>
        <dt className="font-semibold">Error response</dt>
        <dd>
          <pre className="overflow-x-auto font-mono break-all whitespace-pre-wrap">
            {formatError(error)}
          </pre>
        </dd>
      </div>
    </dl>
  </details>
);
